import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, Loader2, Building2, ShieldCheck, Globe2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useI18n } from "@/i18n/I18nContext";
import { DEPARTMENTS } from "@/i18n/extras";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "foreign-consultancy",
  "construction",
  "business-solutions",
  "it-cyber-security",
  "ads-marketing",
  "general",
];

const DEPT_CONFIG = [
  { key: "general", icon: Building2, accent: "navy" },
  { key: "it", icon: ShieldCheck, accent: "gold" },
  { key: "eu", icon: Globe2, accent: "navy" },
];

const Contact = () => {
  const { dict, locale } = useI18n();
  const c = dict.contact;
  const deptDict = DEPARTMENTS[locale] || DEPARTMENTS.en;

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    service: "general",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) er.full_name = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = true;
    if (!form.company.trim() || form.company.trim().length < 2) er.company = true;
    if (!form.message.trim() || form.message.trim().length < 5) er.message = true;
    if (!SERVICES.includes(form.service)) er.service = true;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim(),
        service: form.service,
        message: form.message.trim(),
        locale,
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success(c.form.successTitle, { description: c.form.successBody });
      setForm({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        service: "general",
        message: "",
      });
    } catch (err) {
      console.error("Contact submit error", err);
      toast.error(c.form.errorTitle, { description: c.form.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-[hsl(var(--background))]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Departments */}
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
            {deptDict.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[hsl(var(--primary))]">
            {deptDict.title}
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed">{deptDict.lead}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {DEPT_CONFIG.map((cfg, idx) => {
            const d = deptDict[cfg.key];
            const Icon = cfg.icon;
            const isGold = cfg.accent === "gold";
            return (
              <motion.div
                key={cfg.key}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
              >
                <Card
                  data-testid={`dept-card-${cfg.key}`}
                  className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-6 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${
                        isGold
                          ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))]"
                          : "bg-[hsl(var(--primary))] text-white"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] font-bold tracking-[0.18em] text-[hsl(var(--accent))] uppercase mt-2 text-right">
                      {d.tag}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl text-[hsl(var(--primary))]">{d.title}</h3>
                  <div className="mt-1 text-sm font-semibold text-slate-800">{d.person}</div>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{d.desc}</p>

                  <div className="gold-rule mt-5" />

                  <div className="mt-5 space-y-2.5">
                    <a
                      href={`tel:${d.phone.replace(/\s+/g, "")}`}
                      className="flex items-center gap-2 text-sm text-slate-800 hover:text-[hsl(var(--primary))]"
                      data-testid={`dept-${cfg.key}-phone`}
                    >
                      <Phone className="h-4 w-4 text-[hsl(var(--accent))]" />
                      <span className="font-medium">{d.phone}</span>
                    </a>
                    {d.emails.map((em) => (
                      <a
                        key={em}
                        href={`mailto:${em}`}
                        className="flex items-center gap-2 text-sm text-slate-800 hover:text-[hsl(var(--primary))] break-all"
                        data-testid={`dept-${cfg.key}-email-${em}`}
                      >
                        <Mail className="h-4 w-4 text-[hsl(var(--accent))]" />
                        <span className="font-medium">{em}</span>
                      </a>
                    ))}
                  </div>

                  <div className="mt-auto pt-5 flex items-center gap-2">
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-slate-200 hover:border-[rgba(201,162,39,0.55)]"
                    >
                      <a href={`tel:${d.phone.replace(/\s+/g, "")}`} data-testid={`dept-${cfg.key}-call-cta`}>
                        <Phone className="mr-1.5 h-3.5 w-3.5" />
                        {deptDict.actionCall}
                      </a>
                    </Button>
                    <Button
                      asChild
                      size="sm"
                      className="rounded-lg bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white"
                    >
                      <a href={`mailto:${d.emails[0]}`} data-testid={`dept-${cfg.key}-email-cta`}>
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                        {deptDict.actionEmail}
                      </a>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Form + office info */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
              {c.eyebrow}
            </div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl leading-[1.08] text-[hsl(var(--primary))]">
              {c.title}
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">{c.lead}</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoTile icon={MapPin} title={c.info.addressTitle} body={c.info.addressBody} />
              <InfoTile icon={Phone} title={c.info.phoneTitle} body={c.info.phoneBody} />
              <InfoTile
                icon={Mail}
                title={c.info.emailTitle}
                body={c.info.emailBody}
                href={`mailto:${c.info.emailBody}`}
                dataTestId="contact-info-email"
              />
              <InfoTile icon={Clock} title={c.info.hoursTitle} body={c.info.hoursBody} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="lg:col-span-7"
          >
            <Card className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={c.form.fullName} required error={errors.full_name}>
                    <Input
                      data-testid="contact-full-name-input"
                      value={form.full_name}
                      onChange={onChange("full_name")}
                      placeholder={c.form.fullName}
                      className="h-11"
                      aria-invalid={!!errors.full_name}
                      required
                    />
                  </Field>
                  <Field label={c.form.email} required error={errors.email}>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="name@company.com"
                      className="h-11"
                      aria-invalid={!!errors.email}
                      required
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={c.form.phone}>
                    <Input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={onChange("phone")}
                      placeholder="+90 5xx xxx xx xx"
                      className="h-11"
                    />
                  </Field>
                  <Field label={c.form.company} required error={errors.company}>
                    <Input
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={onChange("company")}
                      placeholder="Company"
                      className="h-11"
                      aria-invalid={!!errors.company}
                      required
                    />
                  </Field>
                </div>

                <Field label={c.form.service} required error={errors.service}>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-service-select"
                      className="h-11"
                      aria-invalid={!!errors.service}
                    >
                      <SelectValue placeholder={c.form.servicePlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((sv) => (
                        <SelectItem key={sv} value={sv} data-testid={`contact-service-option-${sv}`}>
                          {c.form.services[sv]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={c.form.message} required error={errors.message}>
                  <Textarea
                    data-testid="contact-message-textarea"
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="..."
                    rows={5}
                    aria-invalid={!!errors.message}
                    required
                  />
                </Field>

                <div className="flex items-center justify-between gap-4 pt-2">
                  {submitted ? (
                    <div
                      data-testid="contact-success-toast"
                      className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {c.form.successTitle}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">
                      {c.form.confidential}
                    </span>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {c.form.submitting}
                      </>
                    ) : (
                      <>
                        {c.form.submit}
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
      {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && (
      <div className="mt-1 text-xs text-red-600">
        {required ? "Required" : "Invalid"}
      </div>
    )}
  </div>
);

const InfoTile = ({ icon: Icon, title, body, href, dataTestId }) => {
  const content = (
    <>
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-white">
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mt-3">
        {title}
      </div>
      <div className="text-sm font-medium text-[hsl(var(--primary))] mt-0.5 break-words">
        {body}
      </div>
    </>
  );
  const cls = "rounded-2xl border border-slate-200 bg-white p-4";
  return href ? (
    <a href={href} className={cls + " hover:border-[rgba(201,162,39,0.45)] transition-colors"} data-testid={dataTestId}>
      {content}
    </a>
  ) : (
    <div className={cls} data-testid={dataTestId}>{content}</div>
  );
};

export default Contact;
